function KnifePreview() {
    const [currentShape, setCurrentShape] = React.useState(null);
    const [currentBlade, setCurrentBlade] = React.useState(null);
    const [currentHandle, setCurrentHandle] = React.useState(null);
    const [currentBladeData, setCurrentBladeData] = React.useState(null);
    const [currentHandleData, setCurrentHandleData] = React.useState(null);
    const [shapeLoading, setShapeLoading] = React.useState(false);
    const [bladeLoading, setBladeLoading] = React.useState(false);
    const [handleLoading, setHandleLoading] = React.useState(false);
    
    const shapeTimeoutRef = React.useRef(null);
    const bladeTimeoutRef = React.useRef(null);
    const handleTimeoutRef = React.useRef(null);
    
    const DEFAULT_HANDLE_PREVIEW_SHAPE = 'santoku';
    
    const shapeImages = {
        santoku: 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-santoku.png?v=1769401464',
        nakiri: 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-nakiri.png?v=1769401464',
        gyuto: 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-gyuto.png?v=1769401464',
        bunka: 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-bunka.png?v=1769401464',
        kobunka: 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-kobunka.png?v=1769401464',
        petty: 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-petty.png?v=1769401464',
        'kiritsuke-petty': 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-kiritsuke-petty.png?v=1769401464',
        kiritsuke: 'https://cdn.shopify.com/s/files/1/0947/2773/3619/files/shape-kiritsuke.png?v=1769401464'
    };
    
    const SHAPE_TO_HANDLE_PREVIEW_KEY = {
        'santoku': 'custom.handle_santoku_preview',
        'nakiri': 'custom.handle_nakiri_preview',
        'gyuto': 'custom.handle_gyuto_preview',
        'bunka': 'custom.handle_bunka_preview',
        'kobunka': 'custom.handle_kobunka_preview',
        'petty': 'custom.handle_petty_preview',
        'kiritsuke-petty': 'custom.handle_kiritsuke_petty_preview',
        'kiritsuke': 'custom.handle_kiritsuke_preview'
    };
    
    const TAG_TO_SHAPE = {
        'Santoku': 'santoku',
        'Nakiri': 'nakiri',
        'Gyuto': 'gyuto',
        'Bunka': 'bunka',
        'Kobunka': 'kobunka',
        'Petty': 'petty',
        'Kiritsuke Petty': 'kiritsuke-petty',
        'Kiritsuke': 'kiritsuke'
    };
    
    const isLoading = shapeLoading || bladeLoading || handleLoading;
    
    const preloadImage = (src, type) => {
        return new Promise((resolve, reject) => {
            if (!src) {
                resolve();
                return;
            }
            
            if (type === 'shape' && shapeTimeoutRef.current) {
                clearTimeout(shapeTimeoutRef.current);
                shapeTimeoutRef.current = null;
            } else if (type === 'blade' && bladeTimeoutRef.current) {
                clearTimeout(bladeTimeoutRef.current);
                bladeTimeoutRef.current = null;
            } else if (type === 'handle' && handleTimeoutRef.current) {
                clearTimeout(handleTimeoutRef.current);
                handleTimeoutRef.current = null;
            }
            
            if (type === 'shape') setShapeLoading(true);
            else if (type === 'blade') setBladeLoading(true);
            else if (type === 'handle') setHandleLoading(true);
            
            const img = new Image();
            img.onload = () => {
                const timeoutId = setTimeout(() => {
                    if (type === 'shape') {
                        setShapeLoading(false);
                        shapeTimeoutRef.current = null;
                    } else if (type === 'blade') {
                        setBladeLoading(false);
                        bladeTimeoutRef.current = null;
                    } else if (type === 'handle') {
                        setHandleLoading(false);
                        handleTimeoutRef.current = null;
                    }
                    resolve();
                }, 500);
                
                if (type === 'shape') shapeTimeoutRef.current = timeoutId;
                else if (type === 'blade') bladeTimeoutRef.current = timeoutId;
                else if (type === 'handle') handleTimeoutRef.current = timeoutId;
            };
            img.onerror = () => {
                if (type === 'shape') setShapeLoading(false);
                else if (type === 'blade') setBladeLoading(false);
                else if (type === 'handle') setHandleLoading(false);
                reject();
            };
            img.src = src;
        });
    };
    
    const getShapeFromBladeTags = (tags) => {
        if (!tags || !Array.isArray(tags)) return null;
        for (const tag of tags) {
            if (TAG_TO_SHAPE[tag]) {
                return TAG_TO_SHAPE[tag];
            }
        }
        return null;
    };
    
    const getBladePreviewImage = (bladeData) => {
        if (!bladeData || !bladeData.metafields) return null;
        return bladeData.metafields['custom.blade_customization_preview'] || null;
    };
    
    const getHandlePreviewImage = (handleData, shapeId, bladeData) => {
        if (!handleData || !handleData.metafields) return null;
        
        let effectiveShape = shapeId;
        
        if (!effectiveShape && bladeData) {
            effectiveShape = getShapeFromBladeTags(bladeData.tags);
        }
        
        if (!effectiveShape) {
            effectiveShape = DEFAULT_HANDLE_PREVIEW_SHAPE;
        }
        
        const previewKey = SHAPE_TO_HANDLE_PREVIEW_KEY[effectiveShape];
        if (!previewKey) return null;
        
        return handleData.metafields[previewKey] || 
               handleData.metafields[SHAPE_TO_HANDLE_PREVIEW_KEY[DEFAULT_HANDLE_PREVIEW_SHAPE]] || 
               null;
    };
    
    const getEffectiveShapeForHandle = (shapeId, bladeData) => {
        if (shapeId) return shapeId;
        if (bladeData) {
            const bladeShape = getShapeFromBladeTags(bladeData.tags);
            if (bladeShape) return bladeShape;
        }
        return DEFAULT_HANDLE_PREVIEW_SHAPE;
    };
    
    React.useEffect(() => {
        const checkState = setInterval(() => {
            if (typeof window.KnifeCustomizationState === 'undefined') return;
            
            const newShape = window.KnifeCustomizationState.selectedShape;
            const newBlade = window.KnifeCustomizationState.selectedBlade;
            const newHandle = window.KnifeCustomizationState.selectedHandle;
            const newBladeData = window.KnifeCustomizationState.selectedBladeData;
            const newHandleData = window.KnifeCustomizationState.selectedHandleData;
            
            if (newShape !== currentShape) {
                setCurrentShape(newShape);
                if (newShape && shapeImages[newShape]) {
                    preloadImage(shapeImages[newShape], 'shape');
                } else {
                    setShapeLoading(false);
                }
            }
            
            if (newBlade !== currentBlade || newBladeData !== currentBladeData) {
                setCurrentBlade(newBlade);
                setCurrentBladeData(newBladeData);
                const bladePreviewImg = getBladePreviewImage(newBladeData);
                if (bladePreviewImg) {
                    preloadImage(bladePreviewImg, 'blade');
                } else {
                    setBladeLoading(false);
                }
            }
            
            const currentEffectiveShape = getEffectiveShapeForHandle(currentShape, currentBladeData);
            const newEffectiveShape = getEffectiveShapeForHandle(newShape, newBladeData);
            
            if (newHandle !== currentHandle || 
                newHandleData !== currentHandleData || 
                newEffectiveShape !== currentEffectiveShape) {
                setCurrentHandle(newHandle);
                setCurrentHandleData(newHandleData);
                const handlePreviewImg = getHandlePreviewImage(newHandleData, newShape, newBladeData);
                if (handlePreviewImg) {
                    preloadImage(handlePreviewImg, 'handle');
                } else {
                    setHandleLoading(false);
                }
            }
        }, 100);
        
        return () => clearInterval(checkState);
    }, [currentShape, currentBlade, currentHandle, currentBladeData, currentHandleData]);
    
    React.useEffect(() => {
        return () => {
            if (shapeTimeoutRef.current) clearTimeout(shapeTimeoutRef.current);
            if (bladeTimeoutRef.current) clearTimeout(bladeTimeoutRef.current);
            if (handleTimeoutRef.current) clearTimeout(handleTimeoutRef.current);
        };
    }, []);
    
    const bladePreviewImage = getBladePreviewImage(currentBladeData);
    const handlePreviewImage = getHandlePreviewImage(currentHandleData, currentShape, currentBladeData);
    
    const dropShadowStyle = 'drop-shadow(0 12px 32px rgba(0, 0, 0, 0.1))';
    
    return React.createElement('div', {
        style: { 
            position: 'relative', 
            width: '100%', 
            height: '100%',
            minHeight: '400px',
            display: 'block'
        }
    },
        isLoading && React.createElement('div', { className: 'progress-root' },
            React.createElement('div', { className: 'progress-track' },
                React.createElement('div', { className: 'progress-range' }, '\u00A0')
            )
        ),
        
        React.createElement('div', {
            style: {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: isLoading ? 0.3 : 1,
                transition: 'opacity 0.3s ease'
            }
        },
            currentShape && shapeImages[currentShape] && !currentBlade && !currentHandle && !shapeLoading && React.createElement('img', {
                src: shapeImages[currentShape],
                alt: currentShape + ' knife shape',
                style: {
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: dropShadowStyle
                }
            }),
            
            currentBlade && bladePreviewImage && !bladeLoading && React.createElement('img', {
                src: bladePreviewImage,
                alt: currentBlade + ' blade',
                style: {
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 1,
                    objectFit: 'contain',
                    filter: dropShadowStyle
                }
            }),
            
            currentHandle && handlePreviewImage && !handleLoading && React.createElement('img', {
                src: handlePreviewImage,
                alt: currentHandle + ' handle',
                style: {
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 2,
                    objectFit: 'contain',
                    filter: dropShadowStyle
                }
            }),
            
            !currentShape && !currentBlade && !currentHandle && !isLoading && React.createElement('div', {
                style: {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'rgba(255, 255, 255, 0.2)',
                    fontSize: '14px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                }
            }, 'Select options to preview your knife')
        )
    );
}

window.KnifePreview = KnifePreview;